import { useState } from 'react'
import {
  ChevronDown, ChevronRight, Clock, MessageSquare,
  CheckCircle, AlertTriangle, Star, FileText, Flag
} from 'lucide-react'
import { Badge } from '../../components/ui'

/**
 * TimelineTab — Tab "Timeline" del PEP
 *
 * Vista cronologica del percorso per fasi, con:
 * - Fasi espandibili
 * - Eventi, colloqui, verifiche
 * - Separatore visivo per dataInizioDigitale
 *
 * Props:
 *   timeline     - { pepId, faseCorrente, dataInizioDigitale, items[] }
 *   isCoord      - Boolean
 */

const FASE_ORDER = ['accoglienza', 'osservazione', 'progetto_attivo', 'verifica', 'chiusura']

const FASE_META = {
  accoglienza:     { label: 'Accoglienza',     color: 'blue',   icon: Flag },
  osservazione:    { label: 'Osservazione',    color: 'purple', icon: Clock },
  progetto_attivo: { label: 'Progetto attivo', color: 'green',  icon: Star },
  verifica:        { label: 'Verifica',        color: 'orange', icon: CheckCircle },
  chiusura:        { label: 'Chiusura',        color: 'gray',   icon: FileText }
}

const INDICATORE_ICON = {
  positivo:   <span className="text-green-500">&#9650;</span>,
  negativo:   <span className="text-red-500">&#9660;</span>,
  neutro:     <span className="text-gray-400">&#9679;</span>,
  attenzione: <span className="text-orange-500">&#9888;</span>
}

const TYPE_ICON = {
  evento:     AlertTriangle,
  colloquio:  MessageSquare,
  verifica:   CheckCircle,
  fase:       Flag
}

export default function TimelineTab({ timeline, isCoord }) {
  const [expandedPhases, setExpandedPhases] = useState(() => {
    // Apri la fase corrente di default
    if (timeline?.faseCorrente) {
      return new Set([timeline.faseCorrente])
    }
    return new Set(['accoglienza'])
  })

  if (!timeline || !timeline.items) {
    return (
      <div className="text-center py-12 text-gray-500 text-sm">
        Nessun dato nella timeline
      </div>
    )
  }

  const { items, dataInizioDigitale, faseCorrente } = timeline

  // Difesa in profondità: filtra eventi solo_coordinatore per educatore
  const filteredItems = isCoord
    ? items
    : items.filter(item => item.visibilita !== 'solo_coordinatore')

  // Raggruppa items per fase
  const faseItems = groupByPhase(filteredItems)

  const togglePhase = (fase) => {
    setExpandedPhases(prev => {
      const next = new Set(prev)
      if (next.has(fase)) next.delete(fase)
      else next.add(fase)
      return next
    })
  }

  return (
    <div className="space-y-4">
      {/* Fasi espandibili */}
      {FASE_ORDER.map(faseKey => {
        const meta = FASE_META[faseKey]
        const phaseItems = faseItems[faseKey] || []
        const isExpanded = expandedPhases.has(faseKey)
        const isCurrent = faseKey === faseCorrente
        const hasItems = phaseItems.length > 0

        // Non mostrare fasi vuote non correnti
        if (!hasItems && !isCurrent) return null

        return (
          <FaseSection
            key={faseKey}
            faseKey={faseKey}
            meta={meta}
            items={phaseItems}
            isExpanded={isExpanded}
            isCurrent={isCurrent}
            dataInizioDigitale={dataInizioDigitale}
            onToggle={() => togglePhase(faseKey)}
          />
        )
      })}

      {/* Items non associati a una fase specifica */}
      {faseItems._unassigned && faseItems._unassigned.length > 0 && (
        <div className="space-y-2 mt-4">
          <h4 className="text-sm font-medium text-gray-500">Altri eventi</h4>
          {faseItems._unassigned.map((item, idx) => (
            <TimelineItem key={item._id || idx} item={item} dataInizioDigitale={dataInizioDigitale} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Fase espandibile ────────────────────────────────────────

function FaseSection({ faseKey, meta, items, isExpanded, isCurrent, dataInizioDigitale, onToggle }) {
  const Icon = meta.icon
  const colorMap = {
    blue: 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20',
    purple: 'border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20',
    green: 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20',
    orange: 'border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20',
    gray: 'border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20'
  }
  const headerColorMap = {
    blue: 'text-blue-700 dark:text-blue-400',
    purple: 'text-purple-700 dark:text-purple-400',
    green: 'text-green-700 dark:text-green-400',
    orange: 'text-orange-700 dark:text-orange-400',
    gray: 'text-gray-700 dark:text-gray-400'
  }
  const dotColorMap = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    gray: 'bg-gray-500'
  }

  return (
    <div className={`border rounded-lg overflow-hidden ${isCurrent ? colorMap[meta.color] : 'border-gray-200 dark:border-gray-700'}`}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className={`w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full flex-shrink-0 ${isCurrent ? dotColorMap[meta.color] + ' animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`} />
          <Icon className={`h-4 w-4 flex-shrink-0 ${headerColorMap[meta.color]}`} />
          <span className={`font-semibold text-xs sm:text-sm ${isCurrent ? headerColorMap[meta.color] : 'text-gray-600 dark:text-gray-400'}`}>
            {meta.label}
          </span>
          {isCurrent && (
            <Badge variant="success" className="text-[10px]">In corso</Badge>
          )}
          {items.length > 0 && (
            <span className="text-xs text-gray-400">({items.length})</span>
          )}
        </div>
        {isExpanded
          ? <ChevronDown className="h-4 w-4 text-gray-400" />
          : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>

      {/* Contenuto espanso */}
      {isExpanded && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
          {items.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">
              Nessun evento in questa fase
            </p>
          ) : (
            <div className="relative ml-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-0">
              {renderItemsWithSeparator(items, dataInizioDigitale)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Separatore era storica / digitale ────────────────────────

function DigitaleSeparator() {
  return (
    <div className="relative pl-6 py-3">
      <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-white dark:border-gray-800 z-10" />
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-md">
        <Clock className="h-3.5 w-3.5 text-amber-600" />
        <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
          Inizio registrazione digitale
        </span>
      </div>
    </div>
  )
}

// ── Singolo item ─────────────────────────────────────────────

function TimelineItem({ item }) {
  const IconComponent = TYPE_ICON[item.type] || FileText

  return (
    <div className="relative pl-4 sm:pl-6 py-1.5 sm:py-2 group">
      {/* Dot sulla timeline */}
      <div className={`absolute left-[-5px] top-3.5 sm:top-4 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full border-2 border-white dark:border-gray-800 z-10 ${
        item.isDatoStorico
          ? 'bg-gray-300 dark:bg-gray-600'
          : 'bg-primary-500'
      }`} />

      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg p-2.5 sm:p-3 group-hover:border-primary-200 dark:group-hover:border-primary-700 transition-colors">
        <div className="flex items-start justify-between gap-1.5">
          <div className="flex items-start gap-1.5 sm:gap-2 min-w-0">
            <IconComponent className="h-3.5 sm:h-4 w-3.5 sm:w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                {item.titolo}
              </p>

              {/* Tipo specifico per colloqui/eventi */}
              {item.tipoColloquio && (
                <span className="text-xs text-gray-500">
                  {item.tipoColloquio.replace(/_/g, ' ')}
                </span>
              )}
              {item.tipoEvento && item.tipoEvento !== 'evento_significativo' && (
                <span className="text-xs text-gray-500">
                  {item.tipoEvento.replace(/_/g, ' ')}
                </span>
              )}

              {/* Esito per verifiche */}
              {item.esito && (
                <Badge variant={
                  item.esito === 'raggiunto' ? 'success' :
                  item.esito === 'parziale' ? 'warning' :
                  item.esito === 'non_raggiunto' ? 'error' : 'default'
                } className="text-[10px] mt-1">
                  {item.esito.replace(/_/g, ' ')}
                </Badge>
              )}

              {/* Descrizione/note */}
              {item.descrizione && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.descrizione}</p>
              )}

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {item.tags.map((tag, i) => (
                    <span key={i} className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            {item.indicatore && INDICATORE_ICON[item.indicatore]}
            {item.isDatoStorico && (
              <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded" title="Dato storico">
                storico
              </span>
            )}
          </div>
        </div>

        {/* Data e autore */}
        <div className="flex items-center justify-between mt-2 text-[11px] text-gray-400">
          <span>
            {new Date(item.dataEvento).toLocaleDateString('it-IT', {
              day: 'numeric', month: 'short', year: 'numeric'
            })}
          </span>
          {item.registratoDaNome && <span>{item.registratoDaNome}</span>}
          {item.verificatoDaNome && <span>{item.verificatoDaNome}</span>}
        </div>
      </div>
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────

/**
 * Raggruppa gli items della timeline per fase.
 * Gli items di tipo 'fase' definiscono i range temporali.
 * Gli altri items vengono assegnati alla fase attiva al loro dataEvento.
 */
function groupByPhase(items) {
  // Estrai le fasi ordinate per data
  const faseEntries = items
    .filter(i => i.type === 'fase')
    .sort((a, b) => new Date(a.dataEvento) - new Date(b.dataEvento))

  // Tutti gli items non-fase ordinati cronologicamente (più recente prima)
  const nonFaseItems = items
    .filter(i => i.type !== 'fase')
    .sort((a, b) => new Date(b.dataEvento) - new Date(a.dataEvento))

  const grouped = {}

  for (const item of nonFaseItems) {
    const itemDate = new Date(item.dataEvento)
    let assignedFase = '_unassigned'

    // Trova la fase attiva alla data dell'item
    for (let i = faseEntries.length - 1; i >= 0; i--) {
      if (itemDate >= new Date(faseEntries[i].dataEvento)) {
        assignedFase = faseEntries[i].fase
        break
      }
    }

    if (!grouped[assignedFase]) grouped[assignedFase] = []
    grouped[assignedFase].push(item)
  }

  return grouped
}

/**
 * Inserisce il separatore "era digitale" nella lista items
 */
function renderItemsWithSeparator(items, dataInizioDigitale) {
  if (!dataInizioDigitale) {
    return items.map((item, idx) => <TimelineItem key={item._id || idx} item={item} />)
  }

  const digitaleDate = new Date(dataInizioDigitale)
  let separatorInserted = false
  const elements = []

  // Items sono ordinati dal più recente — il separatore va prima del primo item storico
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const itemDate = new Date(item.dataEvento)

    if (!separatorInserted && itemDate < digitaleDate) {
      elements.push(<DigitaleSeparator key="__separator__" />)
      separatorInserted = true
    }

    elements.push(<TimelineItem key={item._id || i} item={item} />)
  }

  return elements
}
